<script setup lang="ts">
import type { BreadcrumbLink } from '~/types/product.model'

const props = defineProps<{
  links: BreadcrumbLink[]
}>()

useBreadcrumbPageSchema(props.links)

// use this only for tracking only. In anchors `/` should be used for SPA to work
const { origin: mainPageUrl } = useRequestURL()

const trackLinkClick = useTrackLinkClick()

const breadcrumbsTrackingData = { pageLocation: 'Breadcrumbs' } as const

const trackMainPageClick = (): void =>
  trackLinkClick({
    ...breadcrumbsTrackingData,
    linkType: 'Main page',
    linkUrl: mainPageUrl,
  })

const trackBreadcrumbLinkClick = (link: BreadcrumbLink): void => {
  if (!('url' in link) || !link.url) {
    return
  }
  trackLinkClick({
    ...breadcrumbsTrackingData,
    ...link.trackingData,
  })
}
</script>

<template>
  <section class="app-breadcrumbs">
    <h2 class="sr-only">
      Breadcrumb
    </h2>
    <nav class="app-breadcrumbs__nav">
      <ul class="app-breadcrumbs__list list-reset">
        <li class="app-breadcrumbs__list-item">
          <NuxtLink
            class="app-breadcrumbs__home-link app-breadcrumbs__link"
            to="/"
            @click="trackMainPageClick"
          >
            <Icon name="fa6-solid:shop" />
            App Store
          </NuxtLink>
          <span class="app-breadcrumbs__item-spacer"> / </span>
        </li>
        <li
          v-for="(link, index) in links"
          :key="link.name"
          class="app-breadcrumbs__list-item"
        >
          <template v-if="'url' in link && link.url">
            <NuxtLink
              :to="link.url"
              class="app-breadcrumbs__link"
              @click="trackBreadcrumbLinkClick(link)"
            >
              {{ link.name }}
            </NuxtLink>
          </template>
          <template v-else>
            <span class="app-breadcrumbs__link--disabled">
              {{ link.name }}
            </span>
          </template>
          <span
            v-if="index < links.length - 1"
            class="app-breadcrumbs__item-spacer"
          >
            /
          </span>
        </li>
      </ul>
    </nav>
  </section>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/colors' as *;
@use '@/assets/styles/helpers' as *;

.app-breadcrumbs {
  margin: var(--app-spacer-32) 0;
}

.app-breadcrumbs__list {
  display: flex;
  flex-wrap: wrap;
}

.app-breadcrumbs__item-spacer {
  display: inline-block;
  margin: 0 var(--app-spacer-6);
}

.app-breadcrumbs__link {
  @extend %link-color;

  color: var(--app-text-color-primary);
  text-decoration: none;
}

.app-breadcrumbs__link--disabled {
  opacity: 0.85;
}

.app-breadcrumbs__home-link {
  display: inline-flex;
  align-items: center;
  gap: var(--app-spacer-8);
}

.app-breadcrumbs__list-item {
  @extend %font-14-21-regular;

  text-wrap: nowrap;
}
</style>
